import PinSolid from "src/design-system/icons/PinSolid.svg?react";
import BedSolid from "src/design-system/icons/BedSolid.svg?react";
import Transfer from "src/design-system/icons/Transfer.svg?react";

import color from "src/design-system/tokens/color";
import { TransitIcon } from "src/design-system/components/TransitIcon/TransitIcon";
import { tw } from "src/design-system/tw";
import type { Mode } from "../../utils/types/mode";
import { FormattedIsoTime } from "../FormattedIsoTime/FormattedIsoTime";

type TransitType =
  | "origin"
  | "detail"
  | "destination"
  | "interchange"
  | "transfer"
  | "stop"
  | "tripEnd"
  | "accommodation"
  | "joint";

type Props = {
  type: TransitType;
  transitMode: Mode;
  time24Hr?: string;
  isLineJoined?: boolean;
};
type ModeProps = Readonly<{ mode: Mode }>;
type ModeTimeProps = Readonly<{ mode: Mode; time24Hr?: string }>;

export function Timeline(props: Readonly<Props>) {
  return (
    <div
      className="relative flex w-8 shrink-0 flex-col items-center justify-start self-stretch"
      data-testid="timeline-line"
    >
      <TimelineParts {...props} />
    </div>
  );
}

function TimelineParts({ transitMode, ...props }: Readonly<Props>) {
  switch (props.type) {
    case "origin":
      return <OriginTimeline mode={transitMode} time24Hr={props.time24Hr} />;
    case "detail":
      return <DetailTimeline mode={transitMode} />;
    case "destination":
      return (
        <DestinationTimeline
          mode={transitMode}
          time24Hr={props.time24Hr}
          isLineJoined={props.isLineJoined}
        />
      );
    case "stop":
    case "interchange":
      return <InterchangeTimeline mode={transitMode} />;
    case "transfer":
      return <TransferTimeline />;
    case "tripEnd":
      return (
        <TripEndTimeline
          mode={transitMode}
          isLineJoined={props.isLineJoined}
          time24Hr={props.time24Hr}
        />
      );
    case "accommodation":
      return <AccommodationTimeline />;
    case "joint":
      return <JointTimeline />;
    default:
      return null;
  }
}

function OriginTimeline({ mode, time24Hr }: ModeTimeProps) {
  return (
    <div className="flex h-full flex-col items-center">
      {mode === "walk" ? (
        <div className="absolute top-9 bottom-0 flex w-3 flex-shrink-0 justify-center">
          <DottedLine />
        </div>
      ) : (
        <div
          className="absolute top-2 bottom-0 w-3 flex-shrink-0"
          style={{
            backgroundColor: color.transport[mode],
          }}
        />
      )}
      <div
        className="relative top-0.5 mb-1 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border-2 border-white shadow-sm shadow-neutral-alpha-1000"
        style={{
          backgroundColor: color.transport[mode],
        }}
      >
        {!!time24Hr && <TroniconTime time24Hr={time24Hr} />}
        <TransitIcon
          mode={mode}
          className={tw(
            "w-4 text-icon-on-bg-fill",
            mode === "plane" && "rotate-90",
          )}
        />
      </div>
    </div>
  );
}

function DestinationTimeline({
  mode,
  time24Hr,
  isLineJoined = true,
}: ModeTimeProps & { isLineJoined?: boolean }) {
  if (mode === "walk") {
    return (
      <div className="flex h-4 flex-col items-center justify-end">
        <div className="h-2 w-2 rounded-full bg-fill-primary outline-2 outline-neutral-1200" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center">
      {!!time24Hr && <TroniconTime time24Hr={time24Hr} />}
      <div
        className="absolute top-0 flex h-[calc(50%+4px)] w-3 flex-shrink-0 justify-center rounded-full rounded-t-none"
        style={
          isFerryStyles(mode) ?? { backgroundColor: color.transport[mode] }
        }
      >
        <div className="absolute bottom-0.5 h-2 w-2 rounded-full bg-fill-solid"></div>
      </div>
      {isLineJoined && <DottedLine />}
    </div>
  );
}

function TripEndTimeline({
  mode,
  time24Hr,
  isLineJoined,
}: ModeTimeProps & {
  isLineJoined?: boolean;
}) {
  return (
    <div className="flex h-full flex-col items-center justify-center">
      {mode !== "walk" && (
        <div
          className="absolute top-0 flex h-1/2 w-3 flex-shrink-0 justify-center rounded-full rounded-t-none"
          style={
            isFerryStyles(mode) ?? { backgroundColor: color.transport[mode] }
          }
        />
      )}
      {isLineJoined && (
        <div className="absolute top-1/2 bottom-0 mt-1">
          <DottedLine />
        </div>
      )}
      <div className="absolute flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-sm shadow-neutral-alpha-1000">
        {!!time24Hr && <TroniconTime time24Hr={time24Hr} />}
        <PinSolid color={color.bg.brand.fill} width={16} height={16} />
      </div>
    </div>
  );
}

function AccommodationTimeline() {
  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accommodation-blue-400">
      <BedSolid color={color.bg.fill.solid} width={16} />
    </div>
  );
}

function DetailTimeline({ mode }: ModeProps) {
  if (mode === "walk") return <WalkDetailTimeline />;

  return (
    <div
      className="absolute -top-0.5 -bottom-0.5 w-3"
      style={isFerryStyles(mode) ?? { backgroundColor: color.transport[mode] }}
    />
  );
}

function InterchangeTimeline({ mode }: ModeProps) {
  if (["walk", "plane"].includes(mode)) return null;

  return (
    <div
      className="absolute -top-0.5 -bottom-0.5 flex w-3 items-center justify-center"
      style={isFerryStyles(mode) ?? { backgroundColor: color.transport[mode] }}
    >
      <div className="absolute z-1 h-2 w-2 rounded-full bg-fill-solid" />
    </div>
  );
}

function WalkDetailTimeline() {
  return (
    <div className="flex h-full w-8 flex-col items-center justify-around">
      <DottedLine />
    </div>
  );
}

function TransferTimeline() {
  return (
    <div className="flex h-16 w-8 flex-col items-center justify-around">
      <DottedLine />
      <Transfer
        width={20}
        color={color.primitive.neutral1200}
        className="flex-shrink-0"
      />
      <DottedLine />
    </div>
  );
}

function JointTimeline() {
  return (
    <div className="flex h-8 w-8 flex-col items-center justify-around">
      <DottedLine />
    </div>
  );
}

export function DottedLine({
  height = "100%",
  style,
}: Readonly<{ height?: string | number; style?: React.CSSProperties }>) {
  const squareDotsStyles = {
    background: `repeating-linear-gradient(
      to bottom,
      transparent 0px,
      transparent 3px,
      var(--color-neutral-1200) 3px,
      var(--color-neutral-1200) 6px
    )`,
  };

  return (
    <div
      className="w-[3px]"
      style={{
        height: typeof height === "number" ? `${height}px` : height,
        ...squareDotsStyles,
        ...style,
      }}
    />
  );
}

const ferryStyles = {
  background: `repeating-linear-gradient(
    to bottom,
    var(--color-transport-blue-300) 0px,
    var(--color-transport-blue-400) 13px,
    var(--color-transport-blue-300) 36px
  )`,
};
const ferryModes: Mode[] = ["ferry", "carferry", "busferry"];

function isFerry(mode: Mode) {
  return ferryModes.includes(mode);
}
function isFerryStyles(mode: Mode) {
  return isFerry(mode) ? ferryStyles : undefined;
}

function TroniconTime({ time24Hr }: Readonly<{ time24Hr: string }>) {
  return (
    <div className="absolute right-full mr-2 w-17 text-right text-body-lg font-medium whitespace-nowrap md:mr-3">
      <FormattedIsoTime time24Hr={time24Hr} />
    </div>
  );
}
